/**
 * ChatExpiryReminderModal Component
 *
 * Shown when one or more rooms are approaching the server's inactivity
 * auto-deletion threshold. Lists each at-risk room with how many days remain
 * and a per-room Leave button.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useMatrixClient } from '../../context/MatrixContext';
import { useI18n } from '../../context/I18nContext';
import mediaCacheService from '../../services/mediaCacheService';
import logger from '../../utils/logger';
import './ChatExpiryReminderModal.css';

const ChatExpiryReminderModal = ({ rooms, onClose, onRoomLeft }) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const [leavingRoomId, setLeavingRoomId] = useState(null);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleLeave = useCallback(async (roomId) => {
    if (leavingRoomId) return;
    if (!client) return;
    setLeavingRoomId(roomId);
    // Mirror useRoom.leaveRoom: clear cached media before leaving so the
    // room's IndexedDB blobs aren't orphaned after we lose membership.
    try {
      await mediaCacheService.removeMediaForRoom(roomId);
    } catch (cacheErr) {
      logger.error(`[ChatExpiryReminderModal] Failed to clear media cache for room ${roomId}:`, cacheErr);
    }
    try {
      await client.leave(roomId);
      logger.info(`[ChatExpiryReminderModal] Left room ${roomId}`);
      onRoomLeft(roomId);
    } catch (err) {
      logger.error(`[ChatExpiryReminderModal] Failed to leave room ${roomId}:`, err);
    } finally {
      setLeavingRoomId(null);
    }
  }, [client, leavingRoomId, onRoomLeft]);

  const formatDaysRemaining = (days) => {
    if (days <= 0) return t('ChatExpiryReminderModal.expiresNow', 'Expires today');
    if (days === 1) return t('ChatExpiryReminderModal.oneDayLeft', '1 day left');
    return t('ChatExpiryReminderModal.daysLeft', { days }, `${days} days left`);
  };

  const getDaysClass = (days) => {
    if (days <= 3) return 'expiry-badge expiry-badge--critical';
    if (days <= 7) return 'expiry-badge expiry-badge--warning';
    return 'expiry-badge expiry-badge--info';
  };

  return createPortal(
    <div className="chat-expiry-overlay" onClick={handleOverlayClick}>
      <div className="chat-expiry-modal" role="dialog" aria-modal="true" aria-labelledby="expiry-modal-title">

        {/* Header */}
        <div className="chat-expiry-header">
          <div className="chat-expiry-header-icon" aria-hidden="true">⏳</div>
          <h2 id="expiry-modal-title" className="chat-expiry-title">
            {t('ChatExpiryReminderModal.title', 'Chats Expiring Soon')}
          </h2>
          <button
            className="chat-expiry-close"
            onClick={onClose}
            type="button"
            aria-label={t('ChatExpiryReminderModal.close', 'Close')}
          >
            <ion-icon name="close-outline" />
          </button>
        </div>

        {/* Description */}
        <p className="chat-expiry-description">
          {t(
            'ChatExpiryReminderModal.description',
            'The following conversations will be automatically deleted by the server due to inactivity. Send a message to keep them active, or leave to acknowledge.',
          )}
        </p>

        {/* Room list */}
        <div className="chat-expiry-list">
          {rooms.map((room) => (
            <div key={room.id} className="chat-expiry-room-row">
              <div className="chat-expiry-room-info">
                <span className="chat-expiry-room-name">{room.name}</span>
                <span className={getDaysClass(room.daysRemaining)}>
                  {formatDaysRemaining(room.daysRemaining)}
                </span>
              </div>
              <button
                className="chat-expiry-leave-btn"
                type="button"
                onClick={() => handleLeave(room.id)}
                disabled={leavingRoomId === room.id}
                aria-label={t('ChatExpiryReminderModal.leaveAriaLabel', { name: room.name }, `Leave ${room.name}`)}
              >
                {leavingRoomId === room.id
                  ? t('ChatExpiryReminderModal.leaving', 'Leaving…')
                  : t('ChatExpiryReminderModal.leave', 'Leave')}
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="chat-expiry-footer">
          <button
            className="chat-expiry-close-btn"
            type="button"
            onClick={onClose}
          >
            {t('ChatExpiryReminderModal.close', 'Close')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

ChatExpiryReminderModal.propTypes = {
  rooms: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    daysRemaining: PropTypes.number.isRequired,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onRoomLeft: PropTypes.func.isRequired,
};

export default ChatExpiryReminderModal;
